import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api, getCrmTenant, CRM_TENANT_CHANGE_EVENT } from '../lib/api';
import { logger } from '../lib/logger';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import DateModeSelector from '../components/DateModeSelector';
import { toast } from 'sonner';
import { Trophy, RefreshCw, TrendingUp, TrendingDown, Users, Target, Award, Calendar, Medal, Settings, Minus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import i18n from '../i18n';

const i18nDeltaTitle = () => i18n.t('leaderboard.deltaTitle', 'Variazione vs mese calendario precedente');

// ★ 2026-08-02 (Fase 30 F4) — Solo Richard/Eufemia possono modificare i target KPI
const KPI_TARGETS_EDITORS = new Set([
  'legal@housecashflows.com',
  'eufemia@housecashflows.com',
]);

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso; }
}

function fmtEur(v) {
  if (!v) return '€0';
  return '€' + Math.round(v).toLocaleString('it-IT');
}

function PctBar({ pct, target }) {
  const filled = Math.min(100, Math.max(0, pct));
  const isOverTarget = target && pct >= target;
  return (
    <div className="relative w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
      <div
        className={`absolute top-0 left-0 h-full ${isOverTarget ? 'bg-emerald-500' : 'bg-amber-400'}`}
        style={{ width: `${filled}%` }}
      />
      {target && (
        <div
          className="absolute top-0 h-full border-l-2 border-slate-600"
          style={{ left: `${Math.min(100, target)}%` }}
          title={`Target ${target}%`}
        />
      )}
    </div>
  );
}

function RankBadge({ rank }) {
  if (rank === 1) return <span className="text-lg" title="1° posto">🥇</span>;
  if (rank === 2) return <span className="text-lg" title="2° posto">🥈</span>;
  if (rank === 3) return <span className="text-lg" title="3° posto">🥉</span>;
  return <span className="text-sm text-muted-foreground">#{rank}</span>;
}

// ★ 2026-08-02 (Fase 30 F3) — Chip delta % con freccia. Verde se >=0, ambra se <0.
function DeltaChip({ delta }) {
  if (delta === null || delta === undefined) return null;
  const val = Number(delta);
  const isZero = Math.abs(val) < 0.05;
  const isPositive = val > 0;
  const Icon = isZero ? Minus : (isPositive ? TrendingUp : TrendingDown);
  const cls = isZero
    ? 'bg-slate-100 text-slate-600 border-slate-300'
    : (isPositive
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : 'bg-amber-50 text-amber-700 border-amber-200');
  const sign = isZero ? '' : (isPositive ? '+' : '');
  return (
    <span
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md border text-[10px] font-medium ${cls}`}
      title={i18nDeltaTitle()}
      data-testid="delta-chip"
    >
      <Icon className="w-3 h-3" />
      {sign}{val.toFixed(1)}%
    </span>
  );
}

// ★ 2026-08-02 (Fase 30 F1) — Podio settimanale Top 3 Show (Lunedì → Domenica)
// ★ 2026-09-04 (Fase 28) — label dinamica: Top 3 Senior (CRM IT) vs Associate
function WeeklyShowsPodium({ data, loading }) {
  const { t } = useTranslation();
  const podium = data?.podium || [];
  const podiumLabel = data?.mode === 'senior' ? 'Senior' : 'Associate';
  const medalStyles = [
    { bg: 'bg-gradient-to-br from-amber-100 to-amber-50', ring: 'ring-amber-400', text: 'text-amber-800', icon: 'text-amber-500' },
    { bg: 'bg-gradient-to-br from-slate-100 to-slate-50', ring: 'ring-slate-400', text: 'text-slate-800', icon: 'text-slate-500' },
    { bg: 'bg-gradient-to-br from-orange-100 to-orange-50', ring: 'ring-orange-400', text: 'text-orange-800', icon: 'text-orange-600' },
  ];
  return (
    <Card data-testid="weekly-shows-podium-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-serif flex items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-500" />
          {t('leaderboard.weeklyShows', 'Show della settimana')} — Top 3 {podiumLabel}
          <Badge variant="outline" className="text-[10px] ml-1">Lun → Dom</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 w-full" />)}
          </div>
        ) : podium.length === 0 ? (
          <div className="text-sm text-muted-foreground italic py-4 text-center" data-testid="podium-empty">
            {t('leaderboard.podiumEmpty', 'Nessun show registrato questa settimana. Il podio si aggiornerà appena entrerà il primo show.')}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {podium.map((row) => {
              const style = medalStyles[row.rank - 1] || medalStyles[2];
              return (
                <Link
                  to={`/app/agents/${row.agent_id}`}
                  key={row.agent_id}
                  className={`rounded-xl border p-3 flex items-center gap-3 ring-1 ${style.ring} ${style.bg} hover:shadow-md transition-shadow`}
                  data-testid={`podium-rank-${row.rank}`}
                >
                  <div className="relative">
                    <Medal className={`w-9 h-9 ${style.icon}`} strokeWidth={1.5} />
                    <span className={`absolute inset-0 flex items-center justify-center text-xs font-bold ${style.text}`}>
                      {row.rank}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`font-semibold truncate ${style.text}`}>{row.agent_name}</div>
                    <div className="text-xs text-muted-foreground truncate">{row.role_label}</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-bold ${style.text}`} data-testid={`podium-shows-count-${row.rank}`}>
                      {row.shows_count}
                    </div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wide">show</div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PeriodPicker({ startDate, endDate, onStartChange, onEndChange, presets = ['7', '30', '90', '365'], onPreset }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Calendar className="w-4 h-4 text-muted-foreground" />
      <Input type="date" value={startDate} onChange={(e) => onStartChange(e.target.value)} className="h-8 w-36 text-xs" data-testid="lb-start-date" />
      <span className="text-xs text-muted-foreground">a</span>
      <Input type="date" value={endDate} onChange={(e) => onEndChange(e.target.value)} className="h-8 w-36 text-xs" data-testid="lb-end-date" />
      <div className="flex gap-1 ml-2">
        {presets.map((d) => (
          <Button key={d} size="sm" variant="outline" onClick={() => onPreset(parseInt(d, 10))} className="h-7 px-2 text-xs" data-testid={`lb-preset-${d}`}>
            {d === '7' ? '7g' : d === '30' ? '30g' : d === '90' ? '90g' : '1y'}
          </Button>
        ))}
      </div>
    </div>
  );
}

export default function LeaderboardPage() {
  const { t } = useTranslation();
  // ★ 2026-09-04 (Fase 28) — CRM IT: il ruolo Associato non esiste più →
  // la classifica IT mostra SOLO i Senior (tab Associate nascosta).
  const [tenant, setTenant] = useState(() => getCrmTenant());
  useEffect(() => {
    const onTenantChange = () => setTenant(getCrmTenant());
    window.addEventListener(CRM_TENANT_CHANGE_EVENT, onTenantChange);
    return () => window.removeEventListener(CRM_TENANT_CHANGE_EVENT, onTenantChange);
  }, []);
  const seniorOnly = tenant === 'it';

  const [tab, setTab] = useState(() => (getCrmTenant() === 'it' ? 'senior' : 'associate'));
  useEffect(() => {
    if (seniorOnly && tab !== 'senior') setTab('senior');
  }, [seniorOnly, tab]);
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(iso(new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)));
  const [endDate, setEndDate] = useState(iso(today));
  // ★ 2026-07-30 — date_mode toggle (persist via localStorage per coerenza col Dashboard)
  const [dateMode, setDateMode] = useState(() => {
    try {
      const v = localStorage.getItem('gp_dash_date_mode');
      return v === 'origin' || v === 'activity' ? v : 'activity';
    } catch { return 'activity'; }
  });
  useEffect(() => {
    try {
      localStorage.setItem('gp_dash_date_mode', dateMode);
    } catch (err) {
      // Modalità privata / quota exceeded: non blocchiamo l'UI.
      logger.debug('[LeaderboardPage] localStorage.setItem failed:', err?.message);
    }
  }, [dateMode]);
  const [assocData, setAssocData] = useState(null);
  const [seniorData, setSeniorData] = useState(null);
  const [podiumData, setPodiumData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth() || {};
  const canEditTargets = KPI_TARGETS_EDITORS.has((user?.email || '').toLowerCase());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start_date: startDate, end_date: endDate, date_mode: dateMode };
      // ★ Fase 28 — CRM IT: la classifica Associate non serve più (solo Senior)
      const [a, s, p] = await Promise.all([
        seniorOnly ? Promise.resolve({ data: null }) : api.get('/leaderboard/associate', { params }),
        api.get('/leaderboard/senior', { params }),
        api.get('/leaderboard/weekly-shows-podium'),
      ]);
      setAssocData(a.data);
      setSeniorData(s.data);
      setPodiumData(p.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || i18n.t('leaderboard.loadError', 'Errore caricamento leaderboard'));
    } finally { setLoading(false); }
  }, [startDate, endDate, dateMode, seniorOnly]);

  useEffect(() => { load(); }, [load]);

  const applyPreset = (days) => {
    const end = new Date();
    const start = new Date(end.getTime() - days * 24 * 60 * 60 * 1000);
    setStartDate(iso(start));
    setEndDate(iso(end));
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="leaderboard-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Trophy className="w-6 h-6 text-amber-500" />
          <div>
            <h1 className="text-2xl font-serif text-[hsl(var(--primary))]">{t('leaderboard.title', 'Classifica agenti')}</h1>
            <p className="text-sm text-muted-foreground">{t('leaderboard.subtitle', 'Ranking per performance nel periodo selezionato.')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PeriodPicker
            startDate={startDate} endDate={endDate}
            onStartChange={setStartDate} onEndChange={setEndDate}
            onPreset={applyPreset}
          />
          <div className="h-6 w-px bg-border mx-1 hidden sm:block" />
          <DateModeSelector value={dateMode} onChange={setDateMode} size="sm" testIdPrefix="lb-date-mode" />
          <Button size="sm" variant="outline" onClick={load} data-testid="lb-refresh"><RefreshCw className="w-4 h-4 mr-1" />{t('common.refresh', 'Aggiorna')}</Button>
          {canEditTargets && (
            <Button asChild size="sm" variant="outline" data-testid="lb-open-settings">
              <Link to="/app/settings/kpi-targets" title={t('leaderboard.editTargets', 'Modifica target KPI')}>
                <Settings className="w-4 h-4 mr-1" />Target KPI
              </Link>
            </Button>
          )}
        </div>
      </div>

      {/* ★ 2026-08-02 (Fase 30 F1) — Podio show settimanale (sopra le tab) */}
      <WeeklyShowsPodium data={podiumData} loading={loading} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {/* ★ Fase 28 — CRM IT: solo Senior (il ruolo Associato non esiste più) */}
          {!seniorOnly && (
            <TabsTrigger value="associate" data-testid="lb-tab-associate">
              <Users className="w-4 h-4 mr-1" />Associate
            </TabsTrigger>
          )}
          <TabsTrigger value="senior" data-testid="lb-tab-senior">
            <Award className="w-4 h-4 mr-1" />Senior
          </TabsTrigger>
        </TabsList>

        {/* Associate leaderboard (nascosta nel CRM IT) */}
        {!seniorOnly && (
        <TabsContent value="associate">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-serif flex items-center gap-2">
                  {t('leaderboard.assocByShow', 'Associate · Classifica per Show')}
                  <Badge variant="outline" className="text-[10px]">
                    <Target className="w-3 h-3 mr-0.5" />Target {assocData?.target_pct || 45}%
                  </Badge>
                </CardTitle>
                <div className="text-xs text-muted-foreground">
                  {t('dashboard.period', 'Periodo:')} {fmtDate(startDate)} — {fmtDate(endDate)}
                </div>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {t('leaderboard.assocSortHint', 'Ordinati per numero di show nel periodo. La % Risposta → Show resta come indicatore secondario (target).')}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-4 space-y-2">
                  {Array.from({ length: 4 }).map((_, i) => <Skeleton key={`sk-assoc-${i}`} className="h-10 w-full" />)}
                </div>
              ) : (assocData?.rows || []).length === 0 ? (
                <div className="p-6 text-center text-muted-foreground text-sm">
                  {t('leaderboard.assocEmpty', 'Nessun Associate con lead nel periodo.')}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="lb-associate-table">
                    <thead className="bg-[var(--surface-2)] text-left">
                      <tr>
                        <th className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase w-14">Rank</th>
                        <th className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">{t('leaderboard.colAgent', 'Agente')}</th>
                        <th className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">Lead</th>
                        <th className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">{t('leaderboard.colReplies', 'Risposte')}</th>
                        <th className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">Show</th>
                        <th className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">{t('leaderboard.colConvReplyShow', 'Conv. Risposta→Show')}</th>
                        <th className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">{t('leaderboard.colClosed', 'Chiusi')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(assocData?.rows || []).map((r) => (
                        <tr key={r.agent_id} className="hover:bg-[var(--surface-2)]/50" data-testid={`lb-assoc-row-${r.agent_id}`}>
                          <td className="px-3 py-2"><RankBadge rank={r.rank} /></td>
                          <td className="px-3 py-2">
                            <Link to={`/app/agents/${r.agent_id}`} className="font-medium hover:underline">{r.agent_name}</Link>
                            <div className="text-[10px] text-muted-foreground">{r.role_label}</div>
                          </td>
                          <td className="px-3 py-2 font-semibold">{r.leads}</td>
                          <td className="px-3 py-2 font-semibold text-blue-700" data-testid={`lb-assoc-responses-${r.agent_id}`}>{r.responses ?? 0}</td>
                          <td className="px-3 py-2 font-semibold">{r.shows}</td>
                          <td className="px-3 py-2" data-testid={`lb-assoc-r2s-${r.agent_id}`}>
                            <div className="flex items-center gap-2">
                              <PctBar pct={r.risposta_to_show_pct ?? 0} target={r.target_pct ?? 45} />
                              <span className={`text-xs font-semibold ${r.on_target ? 'text-emerald-600' : 'text-amber-700'}`}>
                                {r.risposta_to_show_pct ?? 0}%
                              </span>
                              {r.on_target ? <TrendingUp className="w-3 h-3 text-emerald-600" /> : <TrendingDown className="w-3 h-3 text-amber-500" />}
                              <DeltaChip delta={r.delta_risposta_to_show_pct} />
                            </div>
                          </td>
                          <td className="px-3 py-2 text-emerald-700 font-semibold">{r.closes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Senior leaderboard */}
        <TabsContent value="senior">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-serif flex items-center gap-2">
                  {t('leaderboard.seniorByRevenue', 'Senior · Classifica per Fatturato')}
                  <Badge variant="outline" className="text-[10px]">
                    <Target className="w-3 h-3 mr-0.5" />{t('leaderboard.closeTarget', 'Target chiusura')} {seniorData?.target_pct || 8}%
                  </Badge>
                </CardTitle>
                <div className="text-xs text-muted-foreground">
                  {t('dashboard.period', 'Periodo:')} {fmtDate(startDate)} — {fmtDate(endDate)}
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                {t('leaderboard.seniorSortHint', 'Ordinati per fatturato nel periodo (chiuso_cliente + chiuso_partner). Il tasso di chiusura è mostrato come indicatore secondario.')}
              </p>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-4 space-y-2">
                  {Array.from({ length: 4 }).map((_, i) => <Skeleton key={`sk-senior-${i}`} className="h-10 w-full" />)}
                </div>
              ) : (seniorData?.rows || []).length === 0 ? (
                <div className="p-6 text-center text-muted-foreground text-sm">
                  {t('leaderboard.seniorEmpty', 'Nessun Senior con show nel periodo.')}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="lb-senior-table">
                    <thead className="bg-[var(--surface-2)] text-left">
                      <tr>
                        <th className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase w-14">Rank</th>
                        <th className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">Senior</th>
                        <th className="px-3 py-2 text-xs font-semibold text-[hsl(var(--primary))] uppercase bg-amber-50">{t('leaderboard.colRevenue', 'Fatturato')}</th>
                        <th className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">{t('leaderboard.colClosed', 'Chiusi')}</th>
                        <th className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">{t('leaderboard.colClientPartner', 'Cliente / Partner')}</th>
                        <th className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">Show</th>
                        <th className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">{t('leaderboard.colConvShowClosed', 'Conv. Show→Chiuso')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(seniorData?.rows || []).map((r) => (
                        <tr key={r.agent_id} className="hover:bg-[var(--surface-2)]/50" data-testid={`lb-senior-row-${r.agent_id}`}>
                          <td className="px-3 py-2"><RankBadge rank={r.rank} /></td>
                          <td className="px-3 py-2">
                            <Link to={`/app/agents/${r.agent_id}`} className="font-medium hover:underline">{r.agent_name}</Link>
                            <div className="text-[10px] text-muted-foreground">{r.role_label}</div>
                          </td>
                          <td className="px-3 py-2 font-bold text-[hsl(var(--primary))] bg-amber-50/60" data-testid={`lb-senior-revenue-${r.agent_id}`}>{fmtEur(r.revenue_eur)}</td>
                          <td className="px-3 py-2 font-semibold text-emerald-700">{r.closes}</td>
                          <td className="px-3 py-2 text-xs">
                            <span className="text-slate-700">{r.closes_cliente || 0}</span>
                            <span className="text-muted-foreground mx-1">/</span>
                            <span className="text-teal-700">{r.closes_partner || 0}</span>
                          </td>
                          <td className="px-3 py-2 font-semibold">{r.shows}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <PctBar pct={r.show_to_close_pct} target={r.target_pct} />
                              <span className={`text-xs font-semibold ${r.on_target ? 'text-emerald-600' : 'text-amber-700'}`}>
                                {r.show_to_close_pct}%
                              </span>
                              {r.on_target ? <TrendingUp className="w-3 h-3 text-emerald-600" /> : <TrendingDown className="w-3 h-3 text-amber-500" />}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
